import { useEffect, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Context } from '../../Context';

import styles from '../../styles/bim.module.scss';

import { AVAILABILITY_STATUS } from '../../constants/availabilityStatus';
import { LAYER_TYPES } from '../../constants/layerTypes';
import { MODEL_SCOPE } from '../../constants/modelScope';
import { MOBILE_VIEW_TYPES } from '../../constants/mobileViewTypes';

const { AVAILABLE, SOLD } = AVAILABILITY_STATUS;
const { BUILDINGS_LAYER, TIERS_LAYER, APARTMENTS_LAYER } = LAYER_TYPES;
const { NO_MODEL, EDIFICE, SITE } = MODEL_SCOPE;
const {	TIERS_VIEW } = MOBILE_VIEW_TYPES;

function OverviewLayer({
	tooltipVisible,
	setTooltipVisible,
	setTooltipContent,
	isSingleEdifice,
	activeFrame,
}) {
	const { t } = useTranslation();

	const {
		dataUrl,
		tiersViewRef,
		isDesktop,
		isMobile,
		setMobileActiveView,
		loading,
		activeBuilding,
		setActiveBuilding,
		setActiveTier,
		activeApartment,
		setActiveApartmentHandle,
		buildingsData,
		displayableApartmentsData: apartmentsData,
		// apartmentsData: displayableApartmentsData,
		tiersData,
		activeLayer,
		setActiveLayer,
		filteredApartmentData,
		isFilterActive,
		overviewLayerRef,
		activeTier,
		updateUrlHash,
		projectSetup,
		currentModelScope,
		setCurrentModelScope,
	} = useContext(Context);

	const [overviewLayer, setOverviewLayer] = useState(null);
	const [activeBuildingData, setActiveBuildingData] = useState(null);

	const listenersRef = useRef([]);
	const framePathsGroupRef = useRef([]);
	const previousActiveFrameIndexRef = useRef(null);
	const lastClickedMobileElementRef = useRef(null);

	const hasSiteThreeDRender = projectSetup?.site?.render_360 ?? false;
	const hasBuildingThreeDRender = projectSetup?.buildings?.render_360 ?? false;
	const modelViewWithThreeDRender = hasSiteThreeDRender || hasBuildingThreeDRender;
	const areSoldAccessible =
		projectSetup?.website?.are_sold_apartments_accessible ?? false;

	/**
	 * Function to count the number of units (apartments) on a specific reference tier.
	 * @param {Array} apartments - Array of apartment objects.
	 * @param {string|number} refTier - The reference tier to count units for.
	 * @param {boolean} countSold - If true, counts sold apartments; if false, excludes them.
	 * @return {number} - The count of units on the specified reference tier.
	 */
	function countUnitsOnRefTier(apartments, refTier, countingForBuilding = false) {
		let tierValueInt = parseInt(refTier);
		let count = 0;

		apartments.forEach((apartment) => {
			let hasApartment = false;
			
			if (countingForBuilding) {
				if (apartment.ref_tier === tierValueInt && apartment.availability !== SOLD) {
					hasApartment = true;
				}
			} else if (apartment.tiers.map((tier) => tier.ref_tier).includes(tierValueInt)) {
				if (apartment.availability !== SOLD) {
					hasApartment = true;
				}
			}
			hasApartment && count++;
		});

		return count;
	}

	function getApartmentTypeLabel(apartment) {
		if (!apartment?.type) return '';
		const firstLetter = apartment.type[0].toUpperCase();
		if (firstLetter === 'M') return t('apartment');
		if (firstLetter === 'U') return t('unit');
		if (firstLetter === 'B') return t('premises');
		return '';
	}

	const updateActiveOverviewFrame = (newActiveFrameIndexStr) => {
		const newActiveFrameIndex = parseInt(newActiveFrameIndexStr, 10);

		if (
			isNaN(newActiveFrameIndex) ||
			!framePathsGroupRef.current ||
			framePathsGroupRef.current.length === 0
		) {
			return;
		}

		const currentGroups = framePathsGroupRef.current;
		const prevActiveIndex = previousActiveFrameIndexRef.current;

		if (
			prevActiveIndex !== null &&
			prevActiveIndex !== newActiveFrameIndex &&
			currentGroups[prevActiveIndex]
		) {
			currentGroups[prevActiveIndex].classList.remove(styles.active);
		}

		if (currentGroups[newActiveFrameIndex]) {
			currentGroups[newActiveFrameIndex].classList.add(styles.active);
			previousActiveFrameIndexRef.current = newActiveFrameIndex;

			// TODO: Tutaj można by potencjalnie wywołać logikę dodawania/usuwania listenerów
			// dla ścieżek <path> wewnątrz nowo aktywowanej grupie (currentGroups[newActiveFrameIndex])
			// i usuwania listenerów ze ścieżek w poprzedniej grupie (currentGroups[prevActiveIndex]),
			// zamiast robić to w jednym dużym useEffect.
		} else {
			console.warn(
				`Overview group for index ${newActiveFrameIndex} not found.`
			);
		}
	};

	async function getLayerSvg() {
		if (currentModelScope === NO_MODEL || !modelViewWithThreeDRender) {
			setOverviewLayer('');
			return;
		}

		let layerName;
		let basePath = dataUrl;

		// // Currently working solution, to be refactored with whole app to use code further down
		// if (currentModelScope === EDIFICE && activeBuilding) {
		// 	basePath += `edifices/${activeBuilding}/`;
		// 	if (activeLayer === BUILDINGS_LAYER || activeLayer === TIERS_LAYER) {
		// 		// layerName = 'tiertwist.svg';
		// 		layerName = 'tiers.svg';
		// 	} else if (activeLayer === APARTMENTS_LAYER) {
		// 		layerName = `${activeBuilding}.svg`; //TODO: change to apartments.svg when new package is ready
		// 	}
		// } else {
		// 	basePath += 'project/';
		// 	if (activeLayer === BUILDINGS_LAYER) {
		// 		layerName = 'buildings.svg';
		// 	} else if (activeLayer === TIERS_LAYER) {
		// 		layerName = 'tiers.svg';
		// 	} else if (activeLayer === APARTMENTS_LAYER) {
		// 		layerName = 'apartments.svg';
		// 	}
		// }

	
    // let layerFileName;
		const activeBuildingId = buildingsData.find((edifice) => edifice.name_original === activeBuilding)?.id_cad;
		const hasEdificeThreeDRenders = projectSetup?.buildings?.render_360 ?? false;
		const isSingleEdifice = buildingsData.length === 1;

    if (currentModelScope === EDIFICE && activeBuildingId && hasEdificeThreeDRenders) {
      basePath += `buildings/${activeBuildingId}/render_360/`; 
    } else {
      basePath += 'site/render_360/';
    }
  
    switch (activeLayer) {
      case BUILDINGS_LAYER: //na ten moment na nazwa, potem pomyślimy overview/edifice trzeba ujednolicic
        if (currentModelScope === SITE && !isSingleEdifice) {
          layerName = 'outlines_buildings.svg';
        }
        break;
      case TIERS_LAYER:
        layerName = 'outlines_tiers.svg';
        break;
      case APARTMENTS_LAYER:
        layerName = 'outlines_apartments.svg';
        break;
      // ... default
    }
  

		if (layerName) {
			try {
				const layerData = await fetch(
					/* webpackMode: "eager" */ `${basePath}${layerName}`
				).then((res) => res.text());
				setOverviewLayer(layerData);
			} catch (error) {
				console.error('Error fetching SVG:', error, {
					basePath,
					layerName,
					activeLayer,
					currentModelScope,
					activeBuilding,
				});
				setOverviewLayer(''); //TODO: Consider setting a default error SVG or message
			}
		} else {
			console.warn(
				`Could not determine SVG layer name for: ${activeLayer} on edifice ${activeBuilding}`
			);
		}
	}

	// Effect to handle event listeners for paths in the overview layer -----------------------------
	useEffect(() => {
		if (!overviewLayerRef.current || !overviewLayer) {
			framePathsGroupRef.current = [];
			previousActiveFrameIndexRef.current = null;
			return;
		}

		previousActiveFrameIndexRef.current = null;
		const svgContainer = overviewLayerRef.current;
		const frameGroupElements = svgContainer?.querySelectorAll('svg > g');

		if (!svgContainer || frameGroupElements.length === 0) {
			console.warn('SVG container not found in OverviewLayer');
			framePathsGroupRef.current = [];
			return;
		}

		framePathsGroupRef.current = Array.from(frameGroupElements);

		// data-frame attribute for debugging or as DOM selector for unit test
		// framePathsGroupRef.current.forEach((element, index) => {
		//   element.setAttribute('data-frame', index.toString());
		// });

		if (activeFrame !== '' && activeFrame !== undefined) {
			updateActiveOverviewFrame(activeFrame);
		} else if (framePathsGroupRef.current.length > 0) {
			//TODO: no activeFrame should never happen if this component mounts, analize why could and decide what to do
		}

		const svgElement = svgContainer.querySelector('svg');
		if (svgElement) {
			svgElement.setAttribute('preserveAspectRatio', 'xMidYMid slice');
		}

		// cleanup on unmount or overviewLayer removal
		return () => {
			if (
				previousActiveFrameIndexRef.current !== null &&
				framePathsGroupRef.current[previousActiveFrameIndexRef.current]
			) {
				framePathsGroupRef.current[
					previousActiveFrameIndexRef.current
				].classList.remove(styles.active);
			}
		};
	}, [overviewLayer, activeFrame]);

	useEffect(() => {
		if (
			activeFrame !== '' &&
			activeFrame !== undefined &&
			framePathsGroupRef.current.length > 0
		) {
			updateActiveOverviewFrame(activeFrame);
		}
	}, [activeFrame]);

	useEffect(() => {
		const edificeName = activeBuilding;
		const activeBuildingDataFromContext = buildingsData.find(
			(edifice) => edifice.name_original == edificeName
		);
		setActiveBuildingData(activeBuildingDataFromContext);
	}, [loading, isSingleEdifice, activeLayer, filteredApartmentData]);

	useEffect(() => {
		getLayerSvg();
	}, [activeBuilding, activeLayer, currentModelScope]);

	// Effect to handle event listeners for paths in the overview layer -----------------------------
	useEffect(() => {
		if (
			!overviewLayerRef.current ||
			!overviewLayer || // whole SVG
			activeFrame === '' ||
			activeFrame === undefined
		) {
			listenersRef.current.forEach(({ element, type, handler }) => {
				element.removeEventListener(type, handler);
			});
			listenersRef.current = [];
			return;
		}

		const activeFrameGroup =
			framePathsGroupRef.current[parseInt(activeFrame, 10)];

		if (!activeFrameGroup) {
			listenersRef.current.forEach(({ element, type, handler }) => {
				element.removeEventListener(type, handler);
			});
			listenersRef.current = [];
			return;
		}
		// get paths only from the <g> element for active animation frame
		const pathsInActiveFrame = Array.from(
			activeFrameGroup.querySelectorAll('path')
		);

		// Clear previous listeners and reset listenersRef
		listenersRef.current.forEach(({ element, type, handler }) => {
			element.removeEventListener(type, handler);
		});
		listenersRef.current = [];

		// all tiers and apartments
		let currentTiers = activeBuilding
			? tiersData.filter((tier) => tier.building === activeBuilding)
			: tiersData;
		let allApartmentsFromData = apartmentsData;
		let visibleFilteredApartments = filteredApartmentData;

		pathsInActiveFrame.forEach((overviewPath) => {
			if (activeLayer === BUILDINGS_LAYER) {
				// EDIFICE LAYER LOGIC ------------------------------------------------------------------------------------------------------------------------
				const edificePath = overviewPath;
				const edificeId = edificePath.getAttribute('id');
				const edificeName = buildingsData.find((edifice) => edifice.id_cad === edificeId)?.name_original;

				if (activeBuilding === edificeName) {
					edificePath.classList.add('active_path');
				} else {
					edificePath.classList.remove('active_path');
				}

				const tiersInEdifice = tiersData
					.filter((tier) => tier.edifice === edificeName)
					.map((tier) => tier.ref_tier);
				const allApartmentsInEdifice = allApartmentsFromData.filter(
					(apartment) => apartment.edifice_name === edificeName
				);
				const filteredApartmentsInEdifice = visibleFilteredApartments.filter(
					(apartment) => apartment.edifice_name === edificeName
				);
				const apartmentsInEdificeCount = tiersInEdifice?.reduce(
					(count, tier) => {
						return (
							count +
							countUnitsOnRefTier(
								allApartmentsInEdifice,
								tier,
								true
							)
						);
					},
					0
				);
				const filteredApartmentsInEdificeCount = tiersInEdifice?.reduce(
					(count, tier) => {
						return (
							count +
							countUnitsOnRefTier(
								filteredApartmentsInEdifice,
								tier,
								true
							)
						);
					},
					0
				);

				const countValueText =
					filteredApartmentsInEdificeCount !== apartmentsInEdificeCount
						? `${filteredApartmentsInEdificeCount}/${apartmentsInEdificeCount}`
						: `${apartmentsInEdificeCount}`;

				const activeBuildingData = buildingsData.find(
					(e) => e.name_original === edificeName
				);
				const edificeDisplayName =
					activeBuildingData?.name_original ||
					activeBuildingData?.name ||
					edificeName;
				const edificeTooltipContent = {
					title: `${t('building')} - ${edificeDisplayName}`,
					apartments: allApartmentsInEdifice.length
						? `${
								isFilterActive ? `${t('filtered_out')}: ` : ''
						  }${countValueText}`
						: `${t('lack_of')}`,
					btntext: `${t('see_building_details')}`,
					setScope: setCurrentModelScope,
				};

				const handleEdificeMouseOver = () => {
					if (!isMobile) {
						setTooltipContent(edificeTooltipContent);
						setTooltipVisible(true);
					}
				};

				const handleEdificeMouseLeave = () => {
					if (!isMobile) {
						setTooltipVisible(false);
						setTooltipContent(null);
					}
				};

				const handleEdificeClick = () => {
					updateUrlHash(`${t('building')}${edificeDisplayName}`);
					setActiveBuilding(edificeName);
					if (isDesktop) {
						setCurrentModelScope(EDIFICE);
						setActiveLayer(APARTMENTS_LAYER);
						setTooltipVisible(false);
						setTooltipContent(null);
					} else if (isMobile) {
						if (lastClickedMobileElementRef.current === edificePath) {
							// setMobileActiveView(TIERS_VIEW);
							setActiveLayer(APARTMENTS_LAYER);
							setCurrentModelScope(EDIFICE);
							setTooltipVisible(false);
							setTooltipContent(null);
							lastClickedMobileElementRef.current = null;
						} else {
							setActiveLayer(BUILDINGS_LAYER);
							setTooltipContent(edificeTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = edificePath;
						}
					}
				};

				edificePath.addEventListener('mouseover', handleEdificeMouseOver);
				listenersRef.current.push({
					element: edificePath,
					type: 'mouseover',
					handler: handleEdificeMouseOver,
				});

				edificePath.addEventListener('mouseleave', handleEdificeMouseLeave);
				listenersRef.current.push({
					element: edificePath,
					type: 'mouseleave',
					handler: handleEdificeMouseLeave,
				});

				edificePath.addEventListener('click', handleEdificeClick);
				listenersRef.current.push({
					element: edificePath,
					type: 'click',
					handler: handleEdificeClick,
				});
			} else if (activeLayer === TIERS_LAYER) {
				// TIERS_LAYER LAYER LOGIC ------------------------------------------------------------------------------------------------------------------------
				const tierPath = overviewPath;
				// const floorIdString = tierPath.getAttribute('id');
				const buildingFloorIdString = tierPath.getAttribute('id'); //in the new package ids are like: "0A-01" being the first floor of the first building (building thats id_cad is 0A)
				const floorIdString = buildingFloorIdString.split('-')[1];
				const buildingIdString = buildingFloorIdString.split('-')[0];
				const buildingOriginalName = buildingsData.find(
					(edifice) => edifice.id_cad === buildingIdString
				)?.name_original;
				const floorIdInt = parseInt(floorIdString, 10);

				const highestSelectableRefTier =
					buildingsData?.reduce((max, edifice) => {
						return Math.max(max, edifice.levels) - 1; // -1 because ref_tier starts from 0 and levels is tiers.length (1-based)
					}, 0) || 0;

				if (
					(activeBuildingData && floorIdInt >= activeBuildingData.levels) ||
					(buildingsData && floorIdInt > highestSelectableRefTier)
				) {
					tierPath.classList.add(styles.not_in_scope);
					return;
				}

				let apartmentsOnTierCount = 0;
				let filteredApartmentsOnTierCount = 0;

				if (currentModelScope === EDIFICE && activeBuilding) {
					const allApartmentsInEdifice = allApartmentsFromData.filter(
						(apartment) => apartment.edifice_name === activeBuilding
					);
					const filteredApartmentsInEdifice = visibleFilteredApartments.filter(
						(apartment) => apartment.edifice_name === activeBuilding
					);
					apartmentsOnTierCount = countUnitsOnRefTier(
						allApartmentsInEdifice,
						floorIdInt
					);
					filteredApartmentsOnTierCount = countUnitsOnRefTier(
						filteredApartmentsInEdifice,
						floorIdInt
					);
				} else {
					apartmentsOnTierCount = countUnitsOnRefTier(
						// allApartmentsFromData,
						allApartmentsFromData.filter((a) => a.edifice_name === buildingOriginalName),
						floorIdInt
					);
					filteredApartmentsOnTierCount = countUnitsOnRefTier(
						// visibleFilteredApartments,
						visibleFilteredApartments.filter((a) => a.edifice_name === buildingOriginalName),
						floorIdInt
					);
					console.log(`Tier ${floorIdInt} in building ${buildingOriginalName}: total ${apartmentsOnTierCount} from:`, allApartmentsFromData.filter((a) => a.edifice_name === buildingOriginalName));
					console.log(`Tier ${floorIdInt} in building ${buildingOriginalName}: total ${apartmentsOnTierCount} from ${allApartmentsFromData.filter((a) => a.edifice_name === buildingOriginalName)}, filtered ${filteredApartmentsOnTierCount}`);
				}

				const countValueText =
					filteredApartmentsOnTierCount !== apartmentsOnTierCount
						? `${filteredApartmentsOnTierCount}/${apartmentsOnTierCount}`
						: `${apartmentsOnTierCount}`;

				const tierData = currentTiers.find((t) => t.ref_tier === floorIdInt);
				const tierName = tierData?.name ?? floorIdString;

				if (activeTier == floorIdString && activeBuilding === buildingOriginalName) {
					tierPath.classList.add('active_path');
				} else {
					tierPath.classList.remove('active_path');
				}

				const tierTooltipContent = {
					title: `${t('tier')} - ${tierName}`,
					building: buildingOriginalName,
					apartments:
						apartmentsOnTierCount !== 0
							? `${
									isFilterActive ? `${t('filtered_out')}: ` : ''
							  }${countValueText}`
							: `${t('lack_of')}`,
					btntext: `${t('see_floor')}`,
				};

				const handleTierMouseOver = () => {
					if (!isMobile) {
						setTooltipContent(tierTooltipContent);
						setTooltipVisible(true);
					}
				};

				const handleTierMouseLeave = () => {
					if (!isMobile) {
						setTooltipVisible(false);
					}
				};

				const handleTierClick = () => {
					setActiveTier(floorIdInt);
					updateUrlHash('tiers');
					setActiveBuilding(buildingOriginalName);

					if (isDesktop) {
						tiersViewRef?.current?.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						});
						setTooltipVisible(false);
						setTooltipContent(null);
						setActiveLayer(TIERS_LAYER);
					} else if (isMobile) {
						if (lastClickedMobileElementRef.current === tierPath) {
							// if(!activeBuilding) setActiveBuilding(buildingsData[0].name_original);
							setActiveTier(floorIdInt);
							setMobileActiveView(TIERS_VIEW);
							setTooltipVisible(false);
							setTooltipContent(null);
							lastClickedMobileElementRef.current = null;
						} else {
							setTooltipContent(tierTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = tierPath;
						}
					}
				};

				tierPath.addEventListener('mouseover', handleTierMouseOver);
				listenersRef.current.push({
					element: tierPath,
					type: 'mouseover',
					handler: handleTierMouseOver,
				});

				tierPath.addEventListener('mouseleave', handleTierMouseLeave);
				listenersRef.current.push({
					element: tierPath,
					type: 'mouseleave',
					handler: handleTierMouseLeave,
				});

				tierPath.addEventListener('click', handleTierClick);
				listenersRef.current.push({
					element: tierPath,
					type: 'click',
					handler: handleTierClick,
				});
			} else if (activeLayer === APARTMENTS_LAYER) {
				// APARTMENT LAYER LOGIC ------------------------------------------------------------------------------------------------------------------------
				const apartmentPath = overviewPath;
				const apartmentUuidFromPath = apartmentPath.getAttribute('id');
				const apartmentIdFromPath = allApartmentsFromData.find((apt) => apt.uuid === apartmentUuidFromPath)?.id_original;

				const apartmentData = allApartmentsFromData.find(
					(apt) => apt.uuid === apartmentUuidFromPath
				);
				const isVisibleAfterFilter = visibleFilteredApartments.some(
					(apt) => apt.uuid === apartmentUuidFromPath
				);

				if (apartmentData) {
					apartmentPath.setAttribute(
						'data-rooms',
						parseInt(apartmentData.rooms)
					);
					// apartmentPath.setAttribute('data-uuid', apartmentData.uuid);
				}

				if (activeApartment === apartmentIdFromPath) {
					apartmentPath.classList.add('active_path');
				} else {
					apartmentPath.classList.remove('active_path');
				}

				if (isVisibleAfterFilter) {
					if (!apartmentData?.availability) {
						apartmentPath.classList.add('rooms-color');
					} else if (apartmentData?.availability === SOLD) {
						apartmentPath.classList.add('state-sell');
					} else {
						apartmentPath.classList.add('rooms-color');
					}
				} else {
					apartmentPath.classList.remove('rooms-color');
					apartmentPath.classList.remove('state-sell');
				}

				let apartmentTooltipContent = {};
				let filteredApartmentTooltipContent = {};
				let isInaccessibleTooltipContent = {
						title: t('inaccessible'), //TODO: when SimilarApartments feature is implemented, inform user about found similar apartments
				};

				if (apartmentData) {
					const apartmentLabel = getApartmentTypeLabel(apartmentData);
					const title = `${apartmentLabel} - ${
						apartmentData.id_original ?? apartmentData.id_original
					}`;
					const surface = `${t('surface')}: ${apartmentData.area_original}`;
					const rooms = `${t('rooms')}: ${apartmentData.rooms}`;
					const availability = apartmentData.availability ?? AVAILABLE;
					const tierForApartmentData = currentTiers.find(
						(t) => t.ref_tier === apartmentData.ref_tier
					);
          const tierNameForApartment =
						tierForApartmentData?.name_original ?? 
						tierForApartmentData?.name ?? 
						String(apartmentData.ref_tier ?? '');

          const buildingTiersForApartmentData = currentTiers.filter(tier => tier.edifice_id === apartmentData.building_id);
					// const tiersForApartmentData = currentTiers.filter(tier => apartmentData.tiers.map(t => t.ref_tier).includes(tier.ref_tier)) || [];
					const tiersForApartmentData = buildingTiersForApartmentData.filter(tier => apartmentData.tiers.map(t => t.ref_tier).includes(tier.ref_tier)) || [];
					const tierNamesForApartment = tiersForApartmentData.map(tier => tier.name_original || tier.ref_tier);
          
					const tierText = tierNamesForApartment.length === 1 
            ? `${t('tier')}: ${tierNameForApartment}`
            : `${t('tiers')}: ${tierNamesForApartment.join(', ')}`;
					const btnText = projectSetup.website.are_sold_apartments_accessible
						? t('see_apartment')
						: availability === SOLD
						? null
						: t('see_apartment');

					apartmentTooltipContent = {
						title: title,
						surface,
						rooms,
						tier: tierText,
						availability,
						btntext: btnText,
						id: apartmentIdFromPath,
						behavior: 1,
					};

					filteredApartmentTooltipContent = {
						// TODO: if scrollTo filter settings is implemented, inform user that scroll/open will happen
						title: title,
						comment: `${t('unit')} ${t('hidden_by_filter')}`,
					};
				}

				const handleApartmentMouseOver = () => {
					if (isMobile) return;
					
					if (apartmentData) {
						if (!isVisibleAfterFilter) {
							setTooltipContent(filteredApartmentTooltipContent);
						} else {
							setTooltipContent(apartmentTooltipContent);
						}
					} else {
						setTooltipContent(isInaccessibleTooltipContent);
					}
					setTooltipVisible(true);
				};

				const handleApartmentMouseLeave = () => {
					if (!isMobile) {
						setTooltipVisible(false);
						setTooltipContent(null);
					}
				};

				const handleApartmentClick = () => {
					if (!apartmentData) {
						if (isMobile) {
							setTooltipContent(isInaccessibleTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = null;
						} else return; // TODO: in future we will handle this case differently, by showing similar apartments;
					}
					if (!isVisibleAfterFilter) {
						if (isMobile) {
							setTooltipContent(filteredApartmentTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = null;
						} else return; // TODO: possibly scroll to filter settings
					}
					if (apartmentData.availability === SOLD && !areSoldAccessible) {
						return;
					}

					updateUrlHash('apartment');

					if (isDesktop) {
						setTooltipContent(null);
						setTooltipVisible(false);
						setActiveApartmentHandle(apartmentIdFromPath);
					} else if (isMobile) {
						if (lastClickedMobileElementRef.current === apartmentPath) {
							setActiveApartmentHandle(apartmentIdFromPath);
							lastClickedMobileElementRef.current = null;
						} else {
							setTooltipContent(apartmentTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = apartmentPath;
						}
					}
				};

				apartmentPath.addEventListener('mouseover', handleApartmentMouseOver);
				listenersRef.current.push({
					element: apartmentPath,
					type: 'mouseover',
					handler: handleApartmentMouseOver,
				});

				apartmentPath.addEventListener('mouseleave', handleApartmentMouseLeave);
				listenersRef.current.push({
					element: apartmentPath,
					type: 'mouseleave',
					handler: handleApartmentMouseLeave,
				});

				apartmentPath.addEventListener('click', handleApartmentClick);
				listenersRef.current.push({
					element: apartmentPath,
					type: 'click',
					handler: handleApartmentClick,
				});
			}
		});

		const handleOutsideMobileClick = (event) => {
			if (!isMobile || !tooltipVisible) {
				return;
			}

			const clickedElement = event.target;
			const clickedPath = clickedElement.closest('path');

			let shouldCloseTooltip = true;

			if (clickedPath) {
				const isHandledInteractivePath = pathsInActiveFrame.some(
					(p) => p === clickedPath
				);

				if (isHandledInteractivePath) {
					shouldCloseTooltip = false;
				}
			}

			if (shouldCloseTooltip) {
				setTooltipVisible(false);
				setTooltipContent(null);
				if (lastClickedMobileElementRef) {
					lastClickedMobileElementRef.current = null;
				}
			}
		};

		const container = overviewLayerRef.current;
		if (container) {
			container.addEventListener('click', handleOutsideMobileClick);
		}

		return () => {
			listenersRef.current.forEach(({ element, type, handler }) => {
				element.removeEventListener(type, handler);
			});
			listenersRef.current = [];
			if (container) {
				container.removeEventListener('click', handleOutsideMobileClick);
			}
		};
	}, [
		overviewLayer,
		activeFrame,
		activeLayer,
		activeBuilding,
		activeTier,
		activeApartment,
		activeBuildingData,
		tiersData,
		apartmentsData,
		filteredApartmentData,
		isFilterActive,
		isMobile,
	]);

	return (
		<div
			className={`${styles.overview_layer} ${isFilterActive ? 'filtered' : ''}`}
			dangerouslySetInnerHTML={{ __html: overviewLayer }}
			ref={overviewLayerRef}
		/>
	);
}

export default OverviewLayer;
